import React from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useStarredEntities, useStarredEntity } from '@backstage/plugin-catalog-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { Progress } from '@backstage/core-components';
import { normalizeEntityRef, belongsToSystem, getEntityRef } from '../utils/entityHelpers';

const useStyles = makeStyles(theme => ({
  viewAllButton: {
    marginTop: theme.spacing(2),
  },
  dialogContent: {
    minWidth: '500px',
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  starredItem: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  starIcon: {
    color: theme.palette.warning.main,
  },
  captionText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  systemEntity: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  childEntity: {
    padding: theme.spacing(1, 1, 1, 4),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  starredItemContent: {
    flex: 1,
    minWidth: 0,
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  systemChip: {
    marginRight: theme.spacing(1),
    borderColor: theme.palette.success.main,
    color: theme.palette.success.main,
  },
  orphanedChip: {
    marginRight: theme.spacing(1),
    borderColor: theme.palette.grey[500],
    color: theme.palette.text.secondary,
  },
  childBorder: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
}));

/**
 * StarToggleButton component
 * 
 * Renders a button to toggle the starred state of an entity.
 * Clicking the star icon will unstar the entity.
 * 
 * @param entity - The entity for which to toggle the starred state
 * @returns Icon button to toggle starred state
 */
const StarToggleButton = ({ entity }: { entity: Entity }) => {
  const { toggleStarredEntity } = useStarredEntity(entity);
  const classes = useStyles();

  const handleToggleStar = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    toggleStarredEntity();
  };

  return (
    <IconButton 
      size="small" 
      onClick={handleToggleStar}
      title="Click to unstar"
    >
      <StarIcon className={classes.starIcon} />
    </IconButton>
  );
};

interface StarredEntitiesWithLimitProps {
  /** Maximum number of top-level entities to display in the card (default: 5) */
  limit?: number;
}

export const StarredEntitiesWithLimit = ({ limit = 5 }: StarredEntitiesWithLimitProps) => {
  const { starredEntities } = useStarredEntities();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [expandedSystems, setExpandedSystems] = React.useState<Set<string>>(new Set());
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = React.useState<Entity[]>([]);
  const [topLevelItemCount, setTopLevelItemCount] = React.useState(0); // Count of top-level items (systems + orphaned entities, excluding child components)
  const [loading, setLoading] = React.useState(true);
  const classes = useStyles();
  const theme = useTheme();

  const maxDisplay = limit;

  React.useEffect(() => {
    const fetchEntities = async () => {
      if (starredEntities.size === 0) {
        setEntities([]);
        setTopLevelItemCount(0);
        setLoading(false);
        return;
      }

      try {
        const entityRefs = Array.from(starredEntities);
        const response = await catalogApi.getEntitiesByRefs({
          entityRefs,
        });

        const items = response.items.filter(
          (item): item is Entity => item !== undefined
        );

        // Group entities hierarchically: systems with their components
        const systems = items.filter(e => e.kind.toLowerCase() === 'system');
        const starredNonSystems = items.filter(e => e.kind.toLowerCase() !== 'system');
        
        // Sort systems alphabetically
        systems.sort((a, b) => 
          (a.metadata.name || '').localeCompare(b.metadata.name || '')
        );
        
        // For each starred system, fetch ALL its components (even non-starred ones)
        const systemComponentsMap = new Map<string, Entity[]>();
        
        await Promise.all(
          systems.map(async (system) => {
            const systemName = system.metadata.name || '';
            const systemNamespace = system.metadata.namespace || 'default';
            
            // Query for all components that belong to this system
            const componentsResponse = await catalogApi.getEntities({
              filter: {
                'spec.system': [
                  systemName,
                  `${systemNamespace}/${systemName}`,
                ],
              },
            });
            
            const components = componentsResponse.items
              .filter(e => e.kind.toLowerCase() !== 'system')
              .sort((a, b) => 
                (a.metadata.name || '').localeCompare(b.metadata.name || '')
              );
            
            systemComponentsMap.set(systemName, components);
          })
        );
        
        // Build hierarchical structure
        const hierarchicalEntities: Entity[] = [];
        const processedComponentRefs = new Set<string>();
        
        systems.forEach(system => {
          // Add the system
          hierarchicalEntities.push(system);
          
          const systemName = system.metadata.name || '';
          const systemComponents = systemComponentsMap.get(systemName) || [];
          
          // Add all components for this system
          hierarchicalEntities.push(...systemComponents);
          
          // Track all components shown under this system (both starred and non-starred)
          // This prevents duplicates in the orphaned entities section
          // Note: starredNonSystems only contains starred entities, so non-starred 
          // components fetched here will never appear in the orphaned section
          systemComponents.forEach(component => {
            const componentRef = getEntityRef(component);
            processedComponentRefs.add(componentRef);
          });
        });
        
        // Add orphaned entities (starred entities not part of any starred system)
        // Note: starredNonSystems contains ONLY entities that are actually starred
        // We exclude components already shown under their parent system
        const orphanedEntities = starredNonSystems
          .filter(entity => {
            const entityRef = getEntityRef(entity);
            
            // Skip if already processed as part of a system
            if (processedComponentRefs.has(entityRef)) {
              return false;
            }
            
            const systemRef = (entity.spec?.system as string) || '';
            
            // If no system reference, it's orphaned
            if (!systemRef) return true;
            
            // Normalize system reference for comparison with starred systems
            const normalizedSystemRef = normalizeEntityRef(systemRef, 'system');
            
            // Check if the referenced system is in our starred systems
            return !systems.some(s => {
              const starredSystemRef = getEntityRef(s);
              return starredSystemRef === normalizedSystemRef;
            });
          })
          .sort((a, b) => {
            // Define kind priority order for orphaned entities
            const kindOrder: Record<string, number> = {
              'component': 1,
              'api': 2,
              'resource': 3,
              'domain': 4,
              'group': 5,
              'location': 6,
              'user': 7,
              'template': 8,
            };
            
            const aKind = a.kind.toLowerCase();
            const bKind = b.kind.toLowerCase();
            const aOrder = kindOrder[aKind] || 999;
            const bOrder = kindOrder[bKind] || 999;
            
            // First sort by kind
            if (aOrder !== bOrder) {
              return aOrder - bOrder;
            }
            
            // Then by name within same kind
            return (a.metadata.name || '').localeCompare(b.metadata.name || '');
          });
        
        hierarchicalEntities.push(...orphanedEntities);

        setEntities(hierarchicalEntities);
        
        // Set count to match visible top-level items (systems + orphaned entities)
        // This excludes components that are grouped under their parent systems
        setTopLevelItemCount(systems.length + orphanedEntities.length);
      } catch (error) {
        console.error('Failed to fetch starred entities:', error);
        setEntities([]);
        setTopLevelItemCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchEntities();
  }, [starredEntities, catalogApi]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const toggleSystem = (systemName: string) => {
    setExpandedSystems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(systemName)) {
        newSet.delete(systemName);
      } else {
        newSet.add(systemName);
      }
      return newSet;
    });
  };

  // Helper function to format entity name without 'default/' namespace
  const formatEntityName = (entity: Entity) => {
    const namespace = entity.metadata.namespace || 'default';
    const name = entity.metadata.name || '';
    
    // Only remove 'default/' prefix, keep other namespaces
    if (namespace === 'default') {
      return name;
    }
    return `${namespace}/${name}`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader title="Your Starred Entities" />
        <CardContent>
          <Progress />
        </CardContent>
      </Card>
    );
  }

  if (entities.length === 0) {
    return (
      <Card>
        <CardHeader title="Your Starred Entities" />
        <CardContent>
          <Typography className={classes.emptyState}>
            Click the star button on any entity to have it show up here!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const hasMoreThanMax = entities.length > maxDisplay;

  return (
    <>
      <Card>
        <CardHeader
          title="Your Starred Entities"
          subheader={`${topLevelItemCount} starred entities`}
        />
        <Divider />
        <CardContent>
          <List disablePadding>
            {(() => {
              const renderedItems: JSX.Element[] = [];
              let i = 0;
              let itemsRendered = 0;
              
              while (i < entities.length && itemsRendered < maxDisplay) {
                const entity = entities[i];
                const isSystem = entity.kind.toLowerCase() === 'system';
                
                if (isSystem) {
                  // Count children for this system
                  const systemName = entity.metadata.name || '';
                  const children: Entity[] = [];
                  let j = i + 1;
                  
                  while (j < entities.length) {
                    const nextEntity = entities[j];
                    // Stop if we hit another system
                    if (nextEntity.kind.toLowerCase() === 'system') {
                      break;
                    }
                    
                    const nextSystemRef = (nextEntity.spec?.system as string) || '';
                    
                    // Stop if the next entity does not belong to this system
                    if (!belongsToSystem(nextSystemRef, systemName)) {
                      break;
                    }
                    
                    children.push(nextEntity);
                    j++;
                  }
                  
                  const isExpanded = expandedSystems.has(systemName);
                  
                  // Render system with expand button
                  renderedItems.push(
                    <div key={`system-${entity.metadata.uid}`} className={classes.systemEntity} onClick={() => toggleSystem(systemName)}>
                      <div className={classes.starredItemContent}>
                        <EntityRefLink entityRef={entity}>
                          <Typography variant="body1">
                            <Chip 
                              label={entity.kind.toLowerCase()} 
                              size="small" 
                              color="primary"
                              style={{ marginRight: '8px' }}
                            />
                            {entity.spec?.type && (
                              <Chip 
                                label={String(entity.spec.type)} 
                                size="small" 
                                variant="outlined"
                                style={{ 
                                  marginRight: '8px',
                                  borderColor: '#4caf50',
                                  color: '#4caf50'
                                }}
                              />
                            )}
                            {formatEntityName(entity)}
                            {children.length > 0 && (
                              <Chip
                                label={`${children.length} item${children.length !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                style={{ marginLeft: '8px' }}
                              />
                            )}
                          </Typography>
                        </EntityRefLink>
                        {entity.metadata.description && (
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {entity.metadata.description}
                          </Typography>
                        )}
                      </div>
                      {children.length > 0 && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleSystem(systemName); }}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                      <StarToggleButton entity={entity} />
                    </div>
                  );
                  
                  // Render children in Collapse
                  if (children.length > 0) {
                    renderedItems.push(
                      <Collapse key={`collapse-${entity.metadata.uid}`} in={isExpanded} timeout="auto" unmountOnExit>
                        {children.map((childEntity) => (
                          <div key={childEntity.metadata.uid} className={classes.childEntity}>
                            <div className={classes.starredItemContent}>
                              <EntityRefLink entityRef={childEntity}>
                                <Typography variant="body1">
                                  <Chip 
                                    label={childEntity.kind.toLowerCase()} 
                                    size="small" 
                                    color="primary"
                                    style={{ marginRight: '8px' }}
                                  />
                                  {childEntity.spec?.type && (
                                    <Chip 
                                      label={String(childEntity.spec.type)} 
                                      size="small" 
                                      variant="outlined"
                                      style={{ 
                                        marginRight: '8px',
                                        borderColor: '#4caf50',
                                        color: '#4caf50'
                                      }}
                                    />
                                  )}
                                  {formatEntityName(childEntity)}
                                </Typography>
                              </EntityRefLink>
                              {childEntity.metadata.description && (
                                <Typography variant="body2" color="textSecondary" noWrap>
                                  {childEntity.metadata.description}
                                </Typography>
                              )}
                            </div>
                          </div>
                        ))}
                      </Collapse>
                    );
                  }
                  
                  itemsRendered++;
                  i = j; // Skip past the children
                } else {
                  // Orphaned entity (not part of a system)
                  renderedItems.push(
                    <div key={entity.metadata.uid} className={classes.starredItem}>
                      <div className={classes.starredItemContent}>
                        <EntityRefLink entityRef={entity}>
                          <Typography variant="body1">
                            <Chip 
                              label={entity.kind.toLowerCase()} 
                              size="small" 
                              color="primary"
                              style={{ marginRight: '8px' }}
                            />
                            {entity.spec?.type && (
                              <Chip 
                                label={String(entity.spec.type)} 
                                size="small" 
                                variant="outlined"
                                style={{ 
                                  marginRight: '8px',
                                  borderColor: '#4caf50',
                                  color: '#4caf50'
                                }}
                              />
                            )}
                            {formatEntityName(entity)}
                          </Typography>
                        </EntityRefLink>
                        {entity.metadata.description && (
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {entity.metadata.description}
                          </Typography>
                        )}
                      </div>
                      <StarToggleButton entity={entity} />
                    </div>
                  );
                  itemsRendered++;
                  i++;
                }
              }
              
              return renderedItems;
            })()}
          </List>
          {hasMoreThanMax && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              className={classes.viewAllButton}
              onClick={handleOpenDialog}
            >
              View All ({topLevelItemCount} starred)
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          All Starred Entities
          <Typography variant="caption" display="block" className={classes.captionText}>
            {topLevelItemCount} items (components grouped under their systems)
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <List disablePadding>
            {entities && (() => {
              const renderedItems: JSX.Element[] = [];
              let i = 0;

              while (i < entities.length) {
                const entity = entities[i];
                const isSystem = entity.kind.toLowerCase() === 'system';

                if (isSystem) {
                  const systemName = entity.metadata.name || '';
                  const children: Entity[] = [];
                  let j = i + 1;

                  // Find all children that belong to this system
                  while (j < entities.length) {
                    const potentialChild = entities[j];
                    
                    // Stop if we hit another system
                    if (potentialChild.kind.toLowerCase() === 'system') {
                      break;
                    }
                    
                    const childSystemRef = (potentialChild.spec?.system as string) || '';
                    
                    if (belongsToSystem(childSystemRef, systemName)) {
                      children.push(potentialChild);
                      j++;
                    } else {
                      // Found an entity that doesn't belong to this system
                      // Stop here so we don't skip over orphaned entities
                      break;
                    }
                  }

                  const isExpanded = expandedSystems.has(systemName);
                  const itemCount = children.length;

                  // Render the system
                  renderedItems.push(
                    <ListItem
                      key={`system-${entity.metadata.uid}`}
                      button
                      onClick={() => toggleSystem(systemName)}
                      className={classes.listItem}
                      style={{ fontWeight: 600 }}
                    >
                      <ListItemText
                        primary={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Chip 
                              label={entity.kind.toLowerCase()} 
                              size="small" 
                              color="primary"
                            />
                            {entity.spec?.type && (
                              <Chip 
                                label={String(entity.spec.type)} 
                                size="small" 
                                variant="outlined"
                                style={{ 
                                  borderColor: '#4caf50',
                                  color: '#4caf50'
                                }}
                              />
                            )}
                            {itemCount > 0 && (
                              <Chip
                                label={`${itemCount} item${itemCount !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                style={{ 
                                  borderColor: '#9e9e9e',
                                  color: '#616161'
                                }}
                              />
                            )}
                            <Typography variant="body1" component="span">
                              {formatEntityName(entity)}
                            </Typography>
                            <span style={{ marginLeft: 'auto' }}>
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </span>
                          </div>
                        }
                        secondary={entity.metadata.description}
                      />
                    </ListItem>
                  );

                  // Render children in Collapse
                  if (itemCount > 0) {
                    renderedItems.push(
                      <Collapse key={`collapse-${entity.metadata.uid}`} in={isExpanded} timeout="auto" unmountOnExit>
                        {children.map(child => (
                          <ListItem
                            key={child.metadata.uid}
                            button
                            component="a"
                            href={`/catalog/${child.metadata.namespace || 'default'}/${child.kind}/${child.metadata.name}`}
                            className={classes.listItem}
                            style={{
                              paddingLeft: '48px',
                              borderLeft: `3px solid ${theme.palette.primary.main}`,
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }}
                          >
                            <ListItemText
                              primary={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Chip 
                                    label={child.kind.toLowerCase()} 
                                    size="small" 
                                    color="primary"
                                  />
                                  {child.spec?.type && (
                                    <Chip 
                                      label={String(child.spec.type)} 
                                      size="small" 
                                      variant="outlined"
                                      style={{ 
                                        borderColor: '#4caf50',
                                        color: '#4caf50'
                                      }}
                                    />
                                  )}
                                  <Typography variant="body1" component="span">
                                    {formatEntityName(child)}
                                  </Typography>
                                </div>
                              }
                              secondary={child.metadata.description}
                            />
                          </ListItem>
                        ))}
                      </Collapse>
                    );
                  }

                  i = j; // Move past this system and its children
                } else {
                  // Orphaned entity (not part of a system)
                  renderedItems.push(
                    <ListItem
                      key={entity.metadata.uid}
                      button
                      component="a"
                      href={`/catalog/${entity.metadata.namespace || 'default'}/${entity.kind}/${entity.metadata.name}`}
                      className={classes.listItem}
                    >
                      <ListItemText
                        primary={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Chip 
                              label={entity.kind.toLowerCase()} 
                              size="small" 
                              color="primary"
                            />
                            {entity.spec?.type && (
                              <Chip 
                                label={String(entity.spec.type)} 
                                size="small" 
                                variant="outlined"
                                style={{ 
                                  borderColor: '#4caf50',
                                  color: '#4caf50'
                                }}
                              />
                            )}
                            <Typography variant="body1" component="span">
                              {formatEntityName(entity)}
                            </Typography>
                          </div>
                        }
                        secondary={entity.metadata.description}
                      />
                    </ListItem>
                  );
                  i++;
                }
              }

              return renderedItems;
            })()}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
